import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CompanyInfo } from 'src/app/shared/models/company';

export class CashFlowPdfGenerator {
  generatePdf(data: any, companyInfo: CompanyInfo, period: string, startDate: string, endDate: string): jsPDF {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'letter'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let yPosition = 40;

    // Header - Company Name
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(companyInfo?.company_name || 'Cash Flow Statement', margin, yPosition);
    yPosition += 25;

    // Report Subtitle
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Statement of Cash Flows', margin, yPosition);
    
    // Period Label
    const periodStr = period === 'Custom' ? `${startDate} to ${endDate}` : period;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Period: ${periodStr}`, margin, yPosition + 15);
    
    yPosition += 50;

    // Summary Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('Summary Overview', margin, yPosition);
    yPosition += 15;

    const summaryRows = [
      ['Net Operating Cash Flow', `$${(data.netOperating || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Net Investing Cash Flow', `$${(data.netInvesting || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Net Financing Cash Flow', `$${(data.netFinancing || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Net Change in Cash', `$${(data.netChange || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Beginning Cash Balance', `$${(data.beginningCash || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Ending Cash Balance', `$${(data.endingCash || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [['Metric', 'Amount']],
      body: summaryRows,
      margin: { left: margin },
      styles: { fontSize: 10 },
      headStyles: { fillColor: [44, 62, 80] },
      columnStyles: {
        1: { halign: 'right', fontStyle: 'bold' }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 40;

    // Detailed Breakdown - Operating
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Operating Activities', margin, yPosition);
    yPosition += 15;

    const operatingRows = data.operating.map((row: any) => [
      row.name,
      `$${(row.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Account', 'Amount']],
      body: operatingRows,
      margin: { left: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 168, 255] },
      columnStyles: {
        1: { halign: 'right' }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 40;

    // Check for page break before Investing
    if (yPosition > doc.internal.pageSize.getHeight() - 150) {
      doc.addPage();
      yPosition = 40;
    }

    // Detailed Breakdown - Investing
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Investing Activities', margin, yPosition);
    yPosition += 15;

    const investingRows = data.investing.map((row: any) => [
      row.name,
      `$${(row.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Account', 'Amount']],
      body: investingRows,
      margin: { left: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [156, 39, 176] },
      columnStyles: {
        1: { halign: 'right' }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 40;

    // Check for page break before Financing
    if (yPosition > doc.internal.pageSize.getHeight() - 150) {
      doc.addPage();
      yPosition = 40;
    }

    // Detailed Breakdown - Financing
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Financing Activities', margin, yPosition);
    yPosition += 15;

    const financingRows = data.financing.map((row: any) => [
      row.name,
      `$${(row.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Account', 'Amount']],
      body: financingRows,
      margin: { left: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [255, 152, 0] },
      columnStyles: {
        1: { halign: 'right' }
      }
    });

    // Footer
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Generated on ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'center' }
      );
    }

    return doc;
  }
}
